import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import StreamCard from "@/components/StreamCard";
import { AuctionPostDisplay } from "@/components/AuctionPostDisplay";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Video, Calendar, Clock, Gavel, AlertCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { format } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useApprovalStatus } from "@/hooks/useApprovalStatus";
import { fetchStreamsWithProfiles, StreamWithProfile, classifyStream } from "@/lib/streamUtils";

type AuctionPost = Tables<"auction_posts">;
type TenantContact = Pick<
  Tables<"tenant_settings">,
  "auctioneer_name" | "auctioneer_phone" | "auctioneer_email"
>;

const Index = () => {
  const [allStreams, setAllStreams] = useState<StreamWithProfile[]>([]);
  const [auctionPosts, setAuctionPosts] = useState<AuctionPost[]>([]);
  const [tenant, setTenant] = useState<TenantContact | null>(null);
  const { user } = useAuth();
  const { isStaff } = useUserRole();
  const { approvalStatus } = useApprovalStatus();
  const navigate = useNavigate();
  const defaultRoute = user ? (isStaff ? "/dashboard" : "/streams") : "/auth";
  const showPendingBanner = !!user && !isStaff && approvalStatus === "pending";
  const showRejectedBanner = !!user && !isStaff && approvalStatus === "rejected";

  const liveStreams = allStreams
    .filter((s) => classifyStream(s) === "live")
    .sort((a, b) => {
      if (a.is_live !== b.is_live) return a.is_live ? -1 : 1;
      return new Date(a.scheduled_time ?? 0).getTime() - new Date(b.scheduled_time ?? 0).getTime();
    });

  const upcomingStreams = allStreams
    .filter((s) => classifyStream(s) === "upcoming")
    .sort(
      (a, b) =>
        new Date(a.scheduled_time ?? 0).getTime() - new Date(b.scheduled_time ?? 0).getTime()
    )
    .slice(0, 3);

  const pastStreams = allStreams
    .filter((s) => classifyStream(s) === "past")
    .sort(
      (a, b) =>
        new Date(b.scheduled_time ?? b.created_at ?? 0).getTime() -
        new Date(a.scheduled_time ?? a.created_at ?? 0).getTime()
    )
    .slice(0, 3);

  useEffect(() => {
    const fetchStreams = async () => {
      const query = supabase
        .from("streams")
        .select("*")
        .order("scheduled_time", { ascending: false })
        .limit(30);
      const streams = await fetchStreamsWithProfiles(query);
      setAllStreams(streams);
    };

    const fetchAuctionPosts = async () => {
      const [postsRes, tenantRes] = await Promise.all([
        supabase
          .from("auction_posts")
          .select("*")
          .eq("is_published", true)
          .order("auction_date", { ascending: true }),
        supabase
          .from("tenant_settings")
          .select("auctioneer_name, auctioneer_phone, auctioneer_email")
          .eq("id", 1)
          .single(),
      ]);
      setAuctionPosts(postsRes.data ?? []);
      if (tenantRes.data) setTenant(tenantRes.data);
    };

    fetchStreams();
    fetchAuctionPosts();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {(showPendingBanner || showRejectedBanner) && (
        <div className="pt-20 px-4">
          <div className="container mx-auto">
            <Card
              className={`p-4 flex items-start gap-3 border ${
                showPendingBanner
                  ? "border-yellow-500/40 bg-yellow-500/10"
                  : "border-destructive/40 bg-destructive/10"
              }`}
            >
              <AlertCircle
                className={`w-5 h-5 mt-0.5 shrink-0 ${
                  showPendingBanner ? "text-yellow-600" : "text-destructive"
                }`}
              />
              <div className="text-sm">
                {showPendingBanner ? (
                  <>
                    <p className="font-medium text-foreground">
                      Your account is pending approval
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      A staff member will review your account shortly. You'll be
                      able to watch and chat in live auctions once approved.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="font-medium text-foreground">
                      Your account was not approved
                    </p>
                    <p className="text-muted-foreground mt-0.5">
                      Please contact the auctioneer if you believe this is a
                      mistake.
                    </p>
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(30, 27, 75, 0.4), rgba(30, 27, 75, 0.6)), url(/hero-auction.webp)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-hero opacity-20" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-primary/30 backdrop-blur-sm rounded-full border border-primary/50 animate-slide-up shadow-lg">
              <span className="text-sm font-semibold text-white drop-shadow-md">🏛️ Live Auction Platform</span>
            </div>

            <h1
              className="text-5xl md:text-7xl mb-6 animate-slide-up font-bold drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]"
              style={{ animationDelay: "0.1s" }}
            >
              <span className="text-white">Bid Live,</span>
              <br />
              <span className="bg-gradient-primary bg-clip-text text-transparent drop-shadow-[0_4px_12px_rgba(139,92,246,0.5)]">
                Win Amazing Deals
              </span>
            </h1>

            <p
              className="text-xl text-white mb-8 animate-slide-up drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
              style={{ animationDelay: "0.2s" }}
            >
              Bid on our Live Auctions
            </p>

            <div
              className="flex flex-wrap gap-4 justify-center animate-slide-up"
              style={{ animationDelay: "0.3s" }}
            >
              <Button variant="hero" size="lg" className="text-base" onClick={() => navigate(defaultRoute)}>
                <Video className="w-5 h-5" />
                Start Bidding Free
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute top-1/4 left-10 w-20 h-20 bg-primary/20 rounded-full blur-3xl animate-pulse-glow" />
        <div
          className="absolute bottom-1/4 right-10 w-32 h-32 bg-accent/20 rounded-full blur-3xl animate-pulse-glow"
          style={{ animationDelay: "1s" }}
        />
      </section>

      {/* Live Now */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
              <h2 className="text-3xl font-bold">Live Now</h2>
            </div>
            {liveStreams.length > 0 && (
              <Link to="/streams">
                <Button variant="outline" size="sm">View all</Button>
              </Link>
            )}
          </div>

          {liveStreams.length === 0 ? (
            <Card className="p-10 text-center">
              <Video className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground font-medium">No live auctions right now</p>
              <p className="text-sm text-muted-foreground mt-1">Check back soon or browse upcoming auctions below</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveStreams.slice(0, 3).map((stream, idx) => (
                <div key={stream.id} className="animate-slide-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                  <StreamCard
                    id={stream.id}
                    title={stream.title}
                    description={stream.description}
                    streamer={stream.profiles?.username || "Unknown"}
                    viewers={stream.viewer_count || 0}
                    isLive={stream.is_live}
                    thumbnail={stream.thumbnail_url || undefined}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Auction Schedule (posts with full listing detail) */}
      {auctionPosts.length > 0 && tenant && (
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="flex items-center gap-2 mb-8">
              <Gavel className="w-6 h-6 text-primary" />
              <h2 className="text-3xl font-bold">Auction Schedule</h2>
            </div>
            <div className="space-y-6">
              {auctionPosts.map((post) => (
                <AuctionPostDisplay key={post.id} post={post} tenant={tenant} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Streams */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-primary" />
              <h2 className="text-3xl font-bold">Upcoming Auctions</h2>
            </div>
            {upcomingStreams.length > 0 && (
              <Link to="/streams">
                <Button variant="outline" size="sm">View all</Button>
              </Link>
            )}
          </div>

          {upcomingStreams.length === 0 ? (
            <Card className="p-10 text-center">
              <Calendar className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-muted-foreground font-medium">No upcoming auctions scheduled</p>
              <p className="text-sm text-muted-foreground mt-1">Follow us to be notified when new auctions are announced</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingStreams.map((stream, idx) => (
                <div key={stream.id} className="animate-slide-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                  <StreamCard
                    id={stream.id}
                    title={stream.title}
                    description={stream.description}
                    streamer={stream.profiles?.username || "Unknown"}
                    viewers={stream.viewer_count || 0}
                    isLive={false}
                    thumbnail={stream.thumbnail_url || undefined}
                    scheduledTime={
                      stream.scheduled_time
                        ? format(new Date(stream.scheduled_time), "PPP p")
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Past Streams — only shown if any exist */}
      {pastStreams.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-muted-foreground" />
                <h2 className="text-3xl font-bold text-muted-foreground">Past Streams</h2>
              </div>
              <Link to="/streams">
                <Button variant="outline" size="sm">View all</Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {pastStreams.map((stream, idx) => (
                <div key={stream.id} className="animate-slide-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                  <StreamCard
                    id={stream.id}
                    title={stream.title}
                    description={stream.description}
                    streamer={stream.profiles?.username || "Unknown"}
                    viewers={stream.viewer_count || 0}
                    isLive={false}
                    thumbnail={stream.thumbnail_url || undefined}
                    scheduledTime={
                      stream.scheduled_time
                        ? format(new Date(stream.scheduled_time), "PPP")
                        : undefined
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-gradient-hero relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-primary opacity-10" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl mb-6">Ready to Start Bidding?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Join hundreds of bargain hunters at Bridge Street Auctions
            </p>
            <Button variant="hero" size="lg" className="text-lg" onClick={() => navigate(defaultRoute)}>
              Get Started For Free
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Index;
